import { filterClasses } from "@swivl-mono/helpers";
import { TicketPriority, orderedTicketPriorities, ticketPriorityLabels } from "@swivl-mono/types";
import {
  ActionSheet,
  ActionSheetAnimationDirection,
  ActionSheetStack,
  Button,
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  Input,
  Select,
  SelectItem,
  TextArea
} from "@swivl-mono/ui";
import React, { useEffect, useMemo, useState } from "react";
import { IoIosArrowDown } from "react-icons/io";
import { RiDeleteBinLine } from "react-icons/ri";

import { CustomerPickerActionSheet } from "../../../Components/CustomerPickerModal";
import { ASSIGNEE_SCOPE_TOOLTIP, LOCATION_SCOPE_TOOLTIP } from "./assigneeLocationScope";
import { FieldLabelWithInfo } from "./fields/FieldLabelWithInfo";
import { useCreateTicket } from "./useCreateTicket";

interface CreateTicketActionSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const priorityOptions: SelectItem[] = [
  { value: TicketPriority.NONE, label: ticketPriorityLabels[TicketPriority.NONE] },
  { type: "divider" },
  ...orderedTicketPriorities.filter((p) => p !== TicketPriority.NONE).map((p) => ({ value: p, label: ticketPriorityLabels[p] }))
];

export function CreateTicketActionSheet({ isOpen, onClose }: CreateTicketActionSheetProps): React.ReactNode {
  const {
    selectedTemplateId,
    title,
    setTitle,
    description,
    setDescription,
    assigneeMemberIds,
    setAssigneeMemberIds,
    locationId,
    setLocationId,
    priority,
    setPriority,
    selectedCustomer,
    setSelectedCustomer,
    tasks,
    setTasks,
    newTaskText,
    setNewTaskText,
    showTaskInput,
    setShowTaskInput,
    submitting,
    error,
    templates,
    templatesLoading,
    assigneeOptions,
    locationOptions,
    handleTemplateChange,
    handleSubmit
  } = useCreateTicket(onClose);

  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setCustomerPickerOpen(false);
    }
  }, [isOpen]);

  const templateOptions: SelectItem[] = useMemo(() => {
    const items: SelectItem[] = [{ value: "", label: "No Template" }];
    if (templates.length > 0) {
      items.push({ type: "divider" });
      templates.forEach((t) => {
        items.push({ value: String(t.id), label: t.isDefault ? `${t.name} (Default)` : t.name });
      });
    }
    return items;
  }, [templates]);

  const footer = (
    <Button
      variant={ButtonVariant.PRIMARY}
      size={ButtonSize.LARGE}
      color={ButtonColor.ORANGE}
      fullWidth
      onClick={handleSubmit}
      disabled={!title.trim() || submitting}
    >
      {submitting ? "Creating..." : "Confirm"}
    </Button>
  );

  return (
    <ActionSheetStack onCloseAll={onClose}>
      <ActionSheet isOpen={isOpen} onClose={onClose} title="Create Ticket" footer={footer} maxHeight="90dvh">
        <div className="flex flex-col gap-4 overflow-x-hidden pb-4">
          {error && <div className="rounded bg-red-50 p-3 text-sm text-red-600">{error}</div>}

          {/* Template */}
          {templates.length > 0 && (
            <Select
              label="Template"
              hideClear
              placeholder="Select"
              options={templateOptions}
              selectedValues={selectedTemplateId ? [String(selectedTemplateId)] : [""]}
              onChange={(values) => handleTemplateChange(values[0] && values[0] !== "" ? Number(values[0]) : null)}
              disabled={templatesLoading}
            />
          )}

          {/* Title */}
          <div>
            <label className="mb-1.5 block text-sm text-grey-600">
              Title <span className="text-red-500">*</span>
            </label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Input" />
          </div>

          {/* Description */}
          <div>
            <label className="mb-1.5 block text-sm text-grey-600">Description</label>
            <TextArea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Input" />
          </div>

          {/* Location — above Assignee because the assignee options are scoped
              to it (ENG-2961). */}
          <Select
            label={<FieldLabelWithInfo label="Location" tooltip={LOCATION_SCOPE_TOOLTIP} />}
            isSearchable
            hideClear
            placeholder="Select or Search"
            options={locationOptions}
            selectedValues={locationId ? [String(locationId)] : [""]}
            onChange={(values) => setLocationId(values[0] && values[0] !== "" ? Number(values[0]) : null)}
          />

          {/* Assignee */}
          <div>
            <Select
              label={<FieldLabelWithInfo label="Assignee" tooltip={ASSIGNEE_SCOPE_TOOLTIP} />}
              isSearchable
              placeholder="Select or Search"
              options={assigneeOptions}
              selectedValues={assigneeMemberIds.map(String)}
              onChange={(values) => setAssigneeMemberIds(values.map(Number))}
            />
          </div>

          {/* Customer */}
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-grey-600">Customer</label>
            <button
              type="button"
              onClick={() => setCustomerPickerOpen(true)}
              className="flex h-[34px] w-full cursor-pointer items-center rounded-sm border border-grey-400 bg-linear-to-b from-white to-grey-50 pr-3 text-sm transition-all duration-200 ease-in-out hover:border-orange-500"
            >
              <span className={filterClasses("ml-3 flex-1 truncate text-left", selectedCustomer?.name ? "text-black" : "text-grey-500")}>
                {selectedCustomer?.name ?? "Select"}
              </span>
              <IoIosArrowDown className="ml-auto size-4" />
            </button>
          </div>

          {/* Priority */}
          <Select
            label="Priority"
            hideClear
            placeholder="Select"
            options={priorityOptions}
            selectedValues={[priority]}
            onChange={(values) => setPriority((values[0] as TicketPriority) ?? TicketPriority.NONE)}
          />

          {/* Tasks */}
          <div>
            <label className="mb-1.5 block text-sm text-grey-600">Tasks</label>

            {(tasks.length > 0 || showTaskInput) && (
              <div className="mb-2 space-y-3 rounded border border-grey-200 bg-grey-50 p-4">
                {tasks.map((task) => (
                  <div key={task.id} className="flex min-h-[42px] items-center gap-2 rounded border border-grey-400 bg-white px-3">
                    <div className="h-4 w-4 shrink-0 rounded border border-grey-400 bg-white" />
                    <span className="flex-1 text-sm text-black">{task.description}</span>
                    <button
                      type="button"
                      onClick={() => setTasks(tasks.filter((t) => t.id !== task.id))}
                      className="shrink-0 cursor-pointer text-grey-900 hover:text-orange-500"
                    >
                      <RiDeleteBinLine className="h-4 w-4" />
                    </button>
                  </div>
                ))}

                {showTaskInput && (
                  <div className="flex h-[42px] items-center gap-2 rounded border border-orange-500 bg-white px-3">
                    <div className="h-4 w-4 shrink-0 rounded border border-grey-400 bg-white" />
                    <input
                      type="text"
                      value={newTaskText}
                      onChange={(e) => setNewTaskText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.stopPropagation();
                          if (newTaskText.trim()) {
                            setTasks([...tasks, { id: `task-${Date.now()}`, description: newTaskText.trim() }]);
                          } else {
                            setShowTaskInput(false);
                          }
                          setNewTaskText("");
                        } else if (e.key === "Escape") {
                          e.stopPropagation();
                          setShowTaskInput(false);
                          setNewTaskText("");
                        }
                      }}
                      onBlur={() => {
                        if (newTaskText.trim()) {
                          setTasks([...tasks, { id: `task-${Date.now()}`, description: newTaskText.trim() }]);
                        }
                        setNewTaskText("");
                        setShowTaskInput(false);
                      }}
                      placeholder="New Item"
                      className="flex-1 text-sm text-black outline-none placeholder:text-grey-500"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setShowTaskInput(false);
                        setNewTaskText("");
                      }}
                      className="shrink-0 cursor-pointer text-grey-900 hover:text-orange-500"
                    >
                      <RiDeleteBinLine className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            )}
            <Button
              variant={ButtonVariant.GHOST}
              className="rounded-sm border border-grey-200 hover:border-orange-500"
              onClick={() => setShowTaskInput(true)}
            >
              Add New Item
            </Button>
          </div>
        </div>
      </ActionSheet>

      <CustomerPickerActionSheet
        open={customerPickerOpen}
        onClose={() => setCustomerPickerOpen(false)}
        onBack={() => setCustomerPickerOpen(false)}
        onSelect={(customer) => {
          setSelectedCustomer(customer);
          setCustomerPickerOpen(false);
        }}
        onRemove={() => setSelectedCustomer(null)}
        selectedCustomer={selectedCustomer}
        animationDirection={ActionSheetAnimationDirection.RIGHT}
      />
    </ActionSheetStack>
  );
}
